// Client-side combat state shape + pure state-transition reducers
// (Phase 5 Step 15b).
//
// No PixiJS imports — this module is the single unit-testable surface
// for the combat client reducer. `combat-ui.ts` owns the rendering;
// `server-message-reducer.ts` calls into here for abilityResolved /
// damageDealt / damageTaken / mobSpawned / mobDefeated / playerDied
// state updates, then hands the mutated state to the rendering layer.
//
// Hardcoded Phase 5 loadout: slots 1-4 bound to [heavy-strike,
// quick-shot, defensive-stance, heal-self]. Basic-attack is bound to
// Space. Loadout customization is Phase 6; `slotLoadout` is exported
// so tests + the ability bar can read the same constant.

import { getAbility, type AbilityId } from "../../shared/abilities.ts";
import { getMob } from "../../shared/mob-types.ts";

export type CombatClientState = {
  targetId: string | null;
  // available_at epoch-ms per ability. Entries absent → not on cooldown.
  abilityCooldowns: Map<AbilityId, number>;
  selfHp: { current: number; max: number };
  mobHp: Map<string, { current: number; max: number }>;
  playerHp: Map<string, { current: number; max: number }>;
  // Mob world positions. Seeded on mobSpawned; refreshed each combat
  // tick via the Step 16a `expeditionPositions` ephemeral envelope;
  // cleared on mobDefeated.
  mobPositions: Map<string, { x: number; y: number }>;
  // Party-member world positions inside the active expedition. Same
  // broadcast source as mobPositions; empty when not in expedition.
  // Takes precedence over zone-players lookup in EntityPositionResolver.
  playerPositions: Map<string, { x: number; y: number }>;
  // Floating combat-feedback queue. Drained by combat-ui each frame;
  // entries older than FEEDBACK_LIFETIME_MS are GC'd here on each
  // applyFeedbackTick call. Keeps rendering & state decoupled.
  feedback: FeedbackEntry[];
  // Phase 5 Step 17d — latched boss mob id, set when a mobSpawned
  // envelope with isBoss=true lands, cleared when that mob is
  // defeated. Drives the top-center boss HP bar. Null when no live
  // boss is present in the expedition.
  bossMobId: string | null;
};

export type FeedbackEntry = {
  id: string;
  kind: "damage" | "miss" | "heal" | "loot";
  text: string;
  // Entity coordinates resolved by ui from targetId/attackerId; we
  // carry the id so rendering can pick world-space position from the
  // current renderer roster (avoids stale coords in the reducer).
  anchorEntityId: string;
  spawnedAt: number;
};

export const FEEDBACK_LIFETIME_MS = 1000; // contracts:allow-literal — damage number float duration

export const SLOT_LOADOUT: readonly AbilityId[] = [
  "heavy-strike",
  "quick-shot",
  "defensive-stance",
  "heal-self",
];

export const BASIC_ATTACK_ABILITY: AbilityId = "basic-attack";

export function createCombatState(initialMaxHp: number): CombatClientState {
  return {
    targetId: null,
    abilityCooldowns: new Map(),
    selfHp: { current: initialMaxHp, max: initialMaxHp },
    mobHp: new Map(),
    playerHp: new Map(),
    mobPositions: new Map(),
    playerPositions: new Map(),
    feedback: [],
    bossMobId: null,
  };
}

// ---- pure reducers ----

// abilityResolved: attacker's cooldown bumped; if damage > 0 AND this
// client is not the attacker, no self-HP change fires here (damageTaken
// is the authoritative self-HP envelope).
export function applyAbilityResolved(
  state: CombatClientState,
  payload: { abilityId: AbilityId; attackerId: string; targetId: string; damage: number; hit: boolean; ts: number },
  localPlayerId: string,
  now: number,
): void {
  if (payload.attackerId === localPlayerId) {
    const ability = getAbility(payload.abilityId);
    if (ability !== undefined) {
      state.abilityCooldowns.set(payload.abilityId, now + ability.cooldownMs);
    }
  }
  if (payload.hit && payload.damage > 0) {
    state.feedback.push({
      id: `dmg-${payload.ts}-${payload.attackerId}-${payload.targetId}`,
      kind: "damage",
      text: `-${payload.damage}`,
      anchorEntityId: payload.targetId,
      spawnedAt: now,
    });
  } else if (payload.hit && payload.damage < 0) {
    // Heal path — damage is negative; show absolute magnitude.
    state.feedback.push({
      id: `heal-${payload.ts}-${payload.attackerId}-${payload.targetId}`,
      kind: "heal",
      text: `+${-payload.damage}`,
      anchorEntityId: payload.targetId,
      spawnedAt: now,
    });
  }
}

export function applyAbilityMissed(
  state: CombatClientState,
  payload: { abilityId: AbilityId; attackerId: string; ts: number },
  now: number,
): void {
  state.feedback.push({
    id: `miss-${payload.ts}-${payload.attackerId}-${payload.abilityId}`,
    kind: "miss",
    text: "MISS",
    anchorEntityId: payload.attackerId,
    spawnedAt: now,
  });
}

export function applyDamageDealt(
  state: CombatClientState,
  payload: { targetId: string; targetHp: number },
): void {
  // Target is a mob or a player — we may not know max HP here, so
  // preserve max if present. Fallback max clamps to current when
  // absent (mob snapshots seed max via mobSpawned).
  const existing = state.mobHp.get(payload.targetId) ?? state.playerHp.get(payload.targetId);
  if (state.mobHp.has(payload.targetId)) {
    state.mobHp.set(payload.targetId, {
      current: payload.targetHp,
      max: existing?.max ?? payload.targetHp,
    });
    return;
  }
  if (state.playerHp.has(payload.targetId)) {
    state.playerHp.set(payload.targetId, {
      current: payload.targetHp,
      max: existing?.max ?? payload.targetHp,
    });
  }
  // If we've never seen the target, ignore — snapshot will backfill.
}

export function applyDamageTaken(
  state: CombatClientState,
  payload: { targetId: string; selfHp: number },
  localPlayerId: string,
): void {
  if (payload.targetId === localPlayerId) {
    state.selfHp = { current: payload.selfHp, max: state.selfHp.max };
    return;
  }
  const existing = state.playerHp.get(payload.targetId);
  state.playerHp.set(payload.targetId, {
    current: payload.selfHp,
    max: existing?.max ?? payload.selfHp,
  });
}

export function applyMobSpawned(
  state: CombatClientState,
  payload: { mobId: string; mobType?: string; hp: number; position: { x: number; y: number } },
): void {
  state.mobHp.set(payload.mobId, { current: payload.hp, max: payload.hp });
  state.mobPositions.set(payload.mobId, { x: payload.position.x, y: payload.position.y });
  // Step 17d — latch the boss id for the top-center HP bar. Catalog
  // lookup is best-effort: an unrecognized mobType is treated as
  // non-boss. mobType is optional on the payload type guard so older
  // callsites (unit tests) still compile.
  if (payload.mobType !== undefined) {
    const def = getMob(payload.mobType);
    if (def?.isBoss === true) state.bossMobId = payload.mobId;
  }
}

export function applyMobDefeated(state: CombatClientState, payload: { mobId: string }): void {
  state.mobHp.delete(payload.mobId);
  state.mobPositions.delete(payload.mobId);
  if (state.targetId === payload.mobId) state.targetId = null;
  if (state.bossMobId === payload.mobId) state.bossMobId = null;
}

export function applyPlayerDied(
  state: CombatClientState,
  payload: { playerId: string },
  localPlayerId: string,
): void {
  if (payload.playerId === localPlayerId) {
    state.selfHp = { current: 0, max: state.selfHp.max };
  } else {
    const existing = state.playerHp.get(payload.playerId);
    if (existing) existing.current = 0;
  }
}

// Phase 5 Step 16a — refresh mob + party-member positions from the
// ephemeral expeditionPositions broadcast. Replaces the full map so an
// entity that dropped off the alive-set (just died this tick) is also
// removed from the position stream — avoids stale ghosts.
export function applyExpeditionPositions(
  state: CombatClientState,
  payload: {
    members: ReadonlyArray<{ playerId: string; x: number; y: number }>;
    mobs: ReadonlyArray<{ mobId: string; x: number; y: number }>;
  },
): void {
  state.playerPositions.clear();
  for (const m of payload.members) {
    state.playerPositions.set(m.playerId, { x: m.x, y: m.y });
  }
  // Mobs: upsert (don't clear) so a mob that spawned between ticks —
  // or one not yet in the alive broadcast because its tick hadn't
  // run — stays visible. mobDefeated is the authoritative remove path.
  for (const mob of payload.mobs) {
    state.mobPositions.set(mob.mobId, { x: mob.x, y: mob.y });
  }
}

export function applyLootRolled(
  state: CombatClientState,
  payload: { items: Array<{ itemId: string; quantity: number }> },
  localPlayerId: string,
  now: number,
): void {
  for (const item of payload.items) {
    state.feedback.push({
      id: `loot-${now}-${item.itemId}`,
      kind: "loot",
      text: `+${item.quantity} ${item.itemId}`,
      anchorEntityId: localPlayerId,
      spawnedAt: now,
    });
  }
}

// Drops expired feedback entries. Called from the ticker each frame —
// bounded linear scan, filter in place for GC efficiency.
export function tickFeedback(state: CombatClientState, now: number): void {
  if (state.feedback.length === 0) return;
  const cutoff = now - FEEDBACK_LIFETIME_MS;
  let write = 0;
  for (let read = 0; read < state.feedback.length; read++) {
    const entry = state.feedback[read];
    if (entry === undefined) continue;
    if (entry.spawnedAt >= cutoff) {
      state.feedback[write++] = entry;
    }
  }
  state.feedback.length = write;
}

// Target selection — helper so the click handler + reducer share one
// code path (reducer clears target on mobDefeated; click sets it).
export function setTarget(state: CombatClientState, entityId: string | null): void {
  state.targetId = entityId;
}

// Read helpers used by ability bar + input router.
export function isAbilityOnCooldown(
  state: CombatClientState,
  abilityId: AbilityId,
  now: number,
): boolean {
  const availableAt = state.abilityCooldowns.get(abilityId);
  return availableAt !== undefined && availableAt > now;
}

export function cooldownRemainingRatio(
  state: CombatClientState,
  abilityId: AbilityId,
  now: number,
): number {
  const availableAt = state.abilityCooldowns.get(abilityId);
  if (availableAt === undefined) return 0;
  const ability = getAbility(abilityId);
  if (ability === undefined) return 0;
  const remaining = availableAt - now;
  if (remaining <= 0) return 0;
  return Math.min(1, remaining / ability.cooldownMs);
}
